import { useEffect, useState } from "react";
import {
  copyIcon,
  deleteIcon,
  editIcon,
  eyeIcon,
  filterIcon,
  timeLineIcon,
} from "../../assets/icons/SvgIconList";
import DeleteBlockedModal from "../../components/purchase/DeleteBlockedModal";
import ActionIcon from "../../components/purchase/ActionIcon";
import StatusChip from "../../components/purchase/StatusChip";
import { ICDropDown } from "../../core-component/form";
import {
  AdvanceSearch,
  ICButton,
  ICConfirmationModal,
  ICTable,
} from "../../core-component/ui";
import { IPagination } from "../../data/AppInterface";
import { TBitValue } from "../../data/AppType";
import UseEventCallbackHook from "../../hooks/UseEventCallbackHook";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { ISubscriptionPackageFilter } from "../../services/subscription-package/SubscriptionPackageService";
import { ORDER_BY } from "../../utils/AppConstants";
import { AUDIT_LOG_TYPE, BIT_VALUE } from "../../utils/AppEnumerations";
import {
  escapeHtml,
  prepareMessageFromParams,
  toggleStatus,
} from "../../utils/AppFunctions";
import {
  getActiveStatusOptions,
  getPlanTypeLabel,
  getPlatformFilterOptions,
  getVisibilityTypeOptions,
  VISIBILITY_TYPE_CHIP_CLASS,
  VISIBILITY_TYPE_LABEL_KEY,
} from "../../utils/PurchaseConstants";
import AuditLog from "../audit-log/AuditLogView";
import PackageDuplicateModal from "./PackageDuplicateModal";
import PackageEditor from "./PackageEditor";
import {
  activeInactiveSubscriptionPackage,
  deleteSubscriptionPackage,
  getAllSubscriptionPackage,
  getSubscriptionPackageFields,
  isFreeTrialRow,
  ISubscriptionPackageRow,
  SUBSCRIPTION_PACKAGE_FILTER_INITIAL_VALUE,
} from "./SubscriptionPackageViewController";

let timer: ReturnType<typeof setTimeout>;

interface IEditorState {
  id: string;
  readOnly: boolean;
}

const SubscriptionPackageView = () => {
  const { t } = UseTranslationHook();
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ISubscriptionPackageFilter>(SUBSCRIPTION_PACKAGE_FILTER_INITIAL_VALUE);
  const { pagination, setChangePagination } = UsePaginationHook({
    pagination: { perPageRows: 10, currentPage: 1, sortBy: "name", orderBy: ORDER_BY.asc },
  });
  const [list, setList] = useState<ISubscriptionPackageRow[]>([]);
  const [editor, setEditor] = useState<IEditorState>();
  const [duplicateSource, setDuplicateSource] = useState<{ source?: ISubscriptionPackageRow }>();
  const [packageToDelete, setPackageToDelete] = useState<ISubscriptionPackageRow>();
  const [deleteBlock, setDeleteBlock] = useState<{ row: ISubscriptionPackageRow; message: string }>();
  const [packageForHistory, setPackageForHistory] = useState<ISubscriptionPackageRow>();

  const fetchList = (paginationPayload: IPagination) => {
    setIsLoading(true);
    if (timer) clearTimeout(timer);
    timer = setTimeout(async () => {
      await getAllSubscriptionPackage(setList, paginationPayload, setChangePagination, search, filter);
      setIsLoading(false);
    }, 500);
  };

  const fetchFirstPage = UseEventCallbackHook(() => fetchList({ ...pagination, currentPage: 1 }));

  useEffect(() => {
    fetchFirstPage();
  }, [search, filter, fetchFirstPage]);

  const setPackageStatus = async (row: ISubscriptionPackageRow, value: TBitValue) => {
    const isUpdated = await activeInactiveSubscriptionPackage(row.id, value);
    if (isUpdated) setList(toggleStatus(list, row.id));
    return isUpdated;
  };

  const handleStatus = (row: ISubscriptionPackageRow) =>
    setPackageStatus(row, row.is_active === BIT_VALUE.One ? BIT_VALUE.Zero : BIT_VALUE.One);

  const handleDelete = async (confirmed: boolean) => {
    const row = packageToDelete;
    setPackageToDelete(undefined);
    if (!confirmed || !row) return;
    const blockMessage = await deleteSubscriptionPackage(row.id);
    if (blockMessage) {
      setDeleteBlock({ row, message: blockMessage });
      return;
    }
    fetchList(pagination);
  };

  const typeCell = (row: ISubscriptionPackageRow) => (
    <StatusChip
      label={t(VISIBILITY_TYPE_LABEL_KEY[row.visibility_type])}
      className={VISIBILITY_TYPE_CHIP_CLASS[row.visibility_type]}
    />
  );

  const customActionView = (row: ISubscriptionPackageRow) => (
    <div className="flex items-center justify-center w-full gap-2">
      <ActionIcon
        icon={eyeIcon}
        title={t("view_tooltip_icon")}
        onClick={() => setEditor({ id: row.id, readOnly: true })}
      />
      <ActionIcon
        icon={editIcon}
        title={t("edit_tooltip_icon")}
        onClick={() => setEditor({ id: row.id, readOnly: false })}
      />
      {!isFreeTrialRow(row) && (
          <ActionIcon
            icon={copyIcon}
            title={t("duplicate")}
            onClick={() => setDuplicateSource({ source: row })}
          />
        )}
      {!isFreeTrialRow(row) && (
          <ActionIcon
            icon={deleteIcon}
            title={t("delete_tooltip_icon")}
            onClick={() => setPackageToDelete(row)}
          />
        )}
      <ActionIcon
        icon={timeLineIcon}
        title={t("change_history")}
        onClick={() => setPackageForHistory(row)}
      />
    </div>
  );

  const filterView = (
    <div className="w-full grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
      <ICDropDown
        placeholder={t("type")}
        selected={filter.visibility_type ?? undefined}
        options={getVisibilityTypeOptions(t, true)}
        onSelect={(option) => setFilter((previous) => ({ ...previous, visibility_type: option.data_value }))}
      />
      <ICDropDown
        placeholder={t("plan_type")}
        selected={filter.has_desktop ?? undefined}
        options={getPlatformFilterOptions(t)}
        onSelect={(option) => setFilter((previous) => ({ ...previous, has_desktop: option.data_value }))}
      />
      <ICDropDown
        placeholder={t("status")}
        selected={filter.is_active ?? undefined}
        options={getActiveStatusOptions(t)}
        onSelect={(option) => setFilter((previous) => ({ ...previous, is_active: option.data_value }))}
      />
      <ICButton
        type="button"
        onClick={() => setFilter(SUBSCRIPTION_PACKAGE_FILTER_INITIAL_VALUE)}
        className="flex !w-fit"
      >
        <div className="size-5 grid place-self-center">{filterIcon}</div>
        &nbsp;{t("clear")}
      </ICButton>
    </div>
  );

  return (
    <div className="w-full overflow-scroll scrollbar-remove p-4">
      <AdvanceSearch
        handleOnClickAdd={() => setDuplicateSource({})}
        handleSearchChange={setSearch}
        searchText={search}
        filterNode={filterView}
        refreshTable={() => fetchList(pagination)}
        title={t("subscription_package")}
      />
      <ICTable<ISubscriptionPackageRow>
        isLoading={isLoading}
        totalFlex="grid grid-cols-17"
        fields={getSubscriptionPackageFields(
          { action: customActionView, type: typeCell },
          handleStatus,
          t,
        )}
        data={list}
        pagination={pagination}
        handleChangePerPageRows={(perPageRows) =>
          fetchList({ ...pagination, perPageRows, currentPage: 1 })
        }
        handleChangePage={(page) => fetchList({ ...pagination, currentPage: page })}
        handleSort={(field) =>
          fetchList({
            ...pagination,
            sortBy: field.key,
            orderBy: pagination.orderBy === ORDER_BY.desc ? ORDER_BY.asc : ORDER_BY.desc,
          })
        }
      />
      {editor && (
        <PackageEditor
          packageId={editor.id}
          readOnly={editor.readOnly}
          handleClose={(status) => {
            setEditor(undefined);
            if (status) fetchList(pagination);
          }}
        />
      )}
      {duplicateSource && (
        <PackageDuplicateModal
          defaultSource={duplicateSource.source}
          onClose={() => setDuplicateSource(undefined)}
          onCreated={(newPackageId) => {
            setDuplicateSource(undefined);
            fetchList(pagination);
            setEditor({ id: newPackageId, readOnly: false });
          }}
        />
      )}
      {packageToDelete && (
        <ICConfirmationModal
          title={t("delete_subscription_package")}
          message={prepareMessageFromParams(t("delete_message"), [
            [
              "fieldName",
              escapeHtml(`${packageToDelete.name} (${getPlanTypeLabel(t, packageToDelete.platforms)})`),
            ],
          ])}
          onClose={handleDelete}
        />
      )}
      {deleteBlock && (
        <DeleteBlockedModal
          title={t("cant_delete_package")}
          message={deleteBlock.message}
          onClose={() => setDeleteBlock(undefined)}
          onMakeInactive={
            deleteBlock.row.is_active === BIT_VALUE.One
              ? async () => {
                  await setPackageStatus(deleteBlock.row, BIT_VALUE.Zero);
                  setDeleteBlock(undefined);
                }
              : undefined
          }
        />
      )}
      {packageForHistory && (
        <AuditLog
          handleClose={() => setPackageForHistory(undefined)}
          auditLogRecordId={packageForHistory.id}
          auditLogType={AUDIT_LOG_TYPE.SubscriptionPackage}
          titleName={`${packageForHistory.name} (${getPlanTypeLabel(t, packageForHistory.platforms)})`}
        />
      )}
    </div>
  );
};

export default SubscriptionPackageView;
